import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  deleteField,
  Timestamp,
  QueryDocumentSnapshot,
  DocumentData
} from 'firebase/firestore';
import { db } from '../firebase';
import { Teacher, YoklaAssignment, ScannedData, PoolStats, PoolTeacherProgress, StudentIdPrefixFilter } from '../types';
import { QRUP_CONFIGS, getSubGroupForFenn } from '../constants/fennConfig';
import { blankCtxFromExam, getEffectiveBuraxilisQuestionsForStudent } from '../constants/buraxilisConfig';
import { getExamByCollection } from './examService';
import { checkBuraxilisQuestionCollision } from '../utils/assignmentCollisionUtils';
import { getStudentGradingData } from './yoklaService';
import { matchesPrefixFilter, extractUniquePrefixes, PrefixInfo } from '../utils/studentIdPrefixUtils';
import { collectQuestionFields, isBlankGroup, pickStudentField } from '../utils/studentFieldUtils';

// SubGroup'un boş olup olmadığını kontrol et (undefined, null, boş string veya "-" tire)
const isSubGroupEmpty = (subGroup: string | undefined | null): boolean => {
  return !subGroup || subGroup === '-' || subGroup.trim() === '';
};

// SubGroup normalize fonksiyonu (RI -> Rİ, ri -> Rİ, RK -> RK vb.)
const normalizeSubGroup = (sg: string | null | undefined): string | null => {
  if (!sg) return null;
  // Önce uppercase yap, sonra normal I'yı Türkçe İ'ye çevir
  return sg.toUpperCase().replace(/I/g, 'İ');
};

// Grade normalize fonksiyonu (number veya string olabilir, string'e çevir)
const normalizeGrade = (grade: string | number | null | undefined): string => {
  if (grade === null || grade === undefined) return '';
  // Number ise string'e çevir
  if (typeof grade === 'number') {
    return String(grade);
  }
  // String ise olduğu gibi dön
  return String(grade);
};

// Xdil normalize fonksiyonu: "İngilis" -> "İngilis dili", "Rus" -> "Rus dili" vb.
export const normalizeXdil = (xdil: string | null | undefined): string => {
  if (!xdil) return '';
  const normalized = xdil.trim();
  // Kısa formları tam forma çevir
  if (normalized === 'İngilis' || normalized === 'ingilis' || normalized === 'İNGİLİS') {
    return 'İngilis dili';
  }
  if (normalized === 'Fransız' || normalized === 'fransız' || normalized === 'FRANSIZ') {
    return 'Fransız dili';
  }
  if (normalized === 'Alman' || normalized === 'alman' || normalized === 'ALMAN') {
    return 'Alman dili';
  }
  if (normalized === 'Rus' || normalized === 'rus' || normalized === 'RUS') {
    return 'Rus dili';
  }
  // Zaten tam formdaysa olduğu gibi dön
  return normalized;
};

/**
 * Calculate pool statistics from student data
 * Counts how many questions each teacher in the pool has graded
 */
const calculatePoolStats = (
  poolConfig: { teacherIds: string[]; teachers: Array<{ id: string; name: string; fenn: string; isActive: boolean }> },
  students: any[],
  questionNumbers: string[]
): PoolStats => {
  const teacherProgress: PoolTeacherProgress[] = [];

  // Calculate per-teacher progress
  for (const teacher of poolConfig.teachers) {
    let completedCount = 0;

    // Count how many question-student combinations this teacher has graded
    for (const student of students) {
      for (const qNum of questionNumbers) {
        const qField = `${qNum}_c`;
        const qValue = student[qField];

        if (qValue && typeof qValue === 'object' && qValue.hocaId === teacher.id) {
          completedCount++;
        }
      }
    }

    teacherProgress.push({
      teacherId: teacher.id,
      teacherName: teacher.name,
      completedCount,
      isActive: teacher.isActive
    });
  }

  return {
    totalTeachers: poolConfig.teachers.length,
    activeTeachers: poolConfig.teachers.filter(t => t.isActive).length,
    frozenTeachers: poolConfig.teachers.filter(t => !t.isActive).length,
    teacherProgress
  };
};

// Buraxılış statistikaları
const getBuraxilisStats = async (collectionName: string): Promise<YoxlamaStats> => {
  try {
    console.log('📊 Buraxılış statistikaları hesaplanıyor...');
    
    const [scannedSnapshot, assignments, teachers, exam] = await Promise.all([
      getDocs(collection(db, collectionName)),
      getAllAssignments(collectionName),
      getAllTeachers(),
      getExamByCollection(collectionName),
    ]);

    const totalScannedData = scannedSnapshot.size;
    
    // Assignment statistikaları
    const totalAssignments = assignments.length;
    const activeAssignments = assignments.filter(a => a.status === 'active').length;
    const completedAssignments = assignments.filter(a => a.status === 'completed').length;
    const passiveAssignments = assignments.filter(a => a.status === 'passive').length;
    
    // Müəllim statistikaları
    const totalTeachers = teachers.length;
    const activeTeachers = teachers.filter(t => t.isActive !== false).length;
    
    // FENN BAZLI TOPLAM (SADECE ÖĞRETMEN YOKLAMALARI - ADMİN HARİÇ!)
    // Buraxilis'te soru bazlı sayılmalı: Her fenn için öğrenci sayısı x soru sayısı
    let fennBasedTotal = 0;
    let fennBasedCompleted = 0;
    
    assignments.forEach(a => {
      // Admin yoklamalarını sayma!
      if (a.hocaId === 'admin') return;
      
      // Buraxilis'te questionProgress kullanarak soru bazlı hesapla
      if (a.questionProgress && Object.keys(a.questionProgress).length > 0) {
        // Her soru için total ve completed'i topla
        Object.values(a.questionProgress).forEach((qProgress: any) => {
          const qTotal = qProgress.total || 0;
          const qCompleted = qProgress.completed || 0;
          
          if (a.status === 'active') {
            // Aktif atama: Normal total
            fennBasedTotal += qTotal;
          } else if (a.status === 'passive') {
            // Pasif atama: Sadece completed kadar say
            fennBasedTotal += qCompleted;
          }
          
          fennBasedCompleted += qCompleted;
        });
      } else {
        // Eğer questionProgress yoksa, eski mantık (blok imtahanı gibi)
        fennBasedCompleted += a.completedCount || 0;
        
        if (a.status === 'active') {
          // Aktif atama: Normal totalCount
          fennBasedTotal += a.totalCount || 0;
        } else if (a.status === 'passive') {
          // Pasif atama: totalCount = completedCount (yokladığı kadar!)
          fennBasedTotal += a.completedCount || 0;
        }
      }
    });
    
    // SİSTEM KAPASİTESİ BAZLI (Admin + Teacher için)
    // TÜM yoklamaları say (admin + teacher)
    let systemCompleted = 0;
    let adminEmptyPapers = 0; // Admin boş kağıt sayısı
    
    // Sistem kapasitesi: Tüm öğrenciler için tüm sorular
    let totalSystemCapacity = 0;
    
    // Scanned data'dan direkt say (admin boş kağıtları da dahil)
    // Hər şagird üçün xdil-ə görə effective suallar istifadə et (4 xarici dil dublikatını qarşısını al)
    scannedSnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      const gradeNum = typeof data.grade === 'number' ? data.grade : parseInt(data.grade);
      const gradeQuestions = getEffectiveBuraxilisQuestionsForStudent(
        gradeNum,
        data.xdil,
        blankCtxFromExam(exam, { uploaded_at: data.uploaded_at, scanned_at: data.scanned_at })
      );
      
      if (gradeQuestions && gradeQuestions.length > 0) {
        // Bu öğrenci üçün toplam sual sayısı (xdil-filtered)
        const studentTotalQuestions = gradeQuestions.reduce((sum, subj) => sum + subj.questions.length, 0);
        totalSystemCapacity += studentTotalQuestions;
        
        // Yoxlanmış sualları say (hem teacher hem admin)
        gradeQuestions.forEach(subj => {
          subj.questions.forEach(q => {
            const qField = `${q}_c`;
            const qValue = data[qField];
            
            if (qValue && typeof qValue === 'object') {
              systemCompleted++;
              
              // Admin boş kağıt kontrolü
              if (qValue.hocaId === 'admin' && 
                  qValue.note && 
                  qValue.note.includes('Boş sual - Admin tərəfindən təsdiqləndi')) {
                adminEmptyPapers++;
              }
            }
          });
        });
      }
    });
    
    // Assignments'dan da systemCompleted'i kontrol et (tutarlılık için)
    let systemCompletedFromAssignments = 0;
    assignments.forEach(a => {
      systemCompletedFromAssignments += a.completedCount || 0;
    });
    
    // İki yöntem arasında fark varsa, scanned data'yı kullan (daha doğru)
    console.log(`📋 Admin boş kağıt sayısı (scanned data'dan): ${adminEmptyPapers}`);
    console.log(`📊 Fenn bazlı (teacher only): ${fennBasedCompleted}/${fennBasedTotal}`);
    console.log(`📊 Sistem bazlı (scanned data): ${systemCompleted}/${totalSystemCapacity}`);
    console.log(`📊 Sistem bazlı (assignments): ${systemCompletedFromAssignments}/${totalSystemCapacity}`);
    
    const stats: YoxlamaStats = {
      totalScannedData,
      totalAssignments,
      activeAssignments,
      completedAssignments,
      passiveAssignments,
      totalTeachers,
      activeTeachers,
      totalCompleted: fennBasedCompleted,
      totalRemaining: fennBasedTotal - fennBasedCompleted,
      adminEmptyPapers: adminEmptyPapers, // Admin boş kağıt sayısı (scanned data'dan)
      totalFennBased: {
        completed: fennBasedCompleted,
        total: fennBasedTotal,
        percentage: Math.round((fennBasedCompleted / fennBasedTotal) * 100) || 0
      },
      totalSystemBased: {
        completed: systemCompleted, // Scanned data'dan direkt sayılan
        total: totalSystemCapacity,
        percentage: Math.round((systemCompleted / totalSystemCapacity) * 100) || 0
      },
      byClass: {} // Buraxılış üçün sınıf bazlı detay lazım olsa əlavə ediləcək
    };
    
    console.log('✅ Buraxılış statistikaları:', stats);
    return stats;
  } catch (error) {
    console.error('❌ Buraxılış statistikalarında xəta:', error);
    throw error;
  }
};

// Tüm öğretmenleri getir
export const getAllTeachers = async (): Promise<Teacher[]> => {
  try {
    console.log('📚 adminService: Tüm öğretmenler getiriliyor...');
    
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('status', '==', 'teacher'));
    const querySnapshot = await getDocs(q);
    
    const teachers: Teacher[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const phone = data.phoneNumber ?? data.telefon ?? data.PhoneNumber ?? data.phone ?? '';
      teachers.push({
        id: doc.id,
        name: (data.Name ?? data.name ?? '').trim(),
        fenn: data.fenn,
        status: data.status,
        email: data.email || '',
        isActive: data.isActive !== false, // Default true
        phoneNumber: typeof phone === 'string' ? phone.trim() : ''
      });
    });
    
    console.log('✅ adminService: Öğretmenler başarıyla getirildi:', teachers.length);
    return teachers;
  } catch (error) {
    console.error('❌ adminService: Öğretmenler getirilirken hata:', error);
    throw error;
  }
};

// Öğretmen detaylarını getir
export const getTeacherById = async (teacherId: string): Promise<Teacher | null> => {
  try {
    const teacherDoc = await getDoc(doc(db, 'users', teacherId));
    
    if (teacherDoc.exists()) {
      const data = teacherDoc.data();
      const phone = data.phoneNumber ?? data.telefon ?? data.PhoneNumber ?? data.phone ?? '';
      return {
        id: teacherDoc.id,
        name: (data.Name ?? data.name ?? '').trim(),
        fenn: data.fenn,
        status: data.status,
        email: data.email || '',
        isActive: data.isActive !== false,
        phoneNumber: typeof phone === 'string' ? phone.trim() : ''
      };
    }
    
    return null;
  } catch (error) {
    console.error('❌ adminService: Öğretmen getirilirken hata:', error);
    throw error;
  }
};

// Öğretmeni pasif/aktif et
export const toggleTeacherStatus = async (teacherId: string, isActive: boolean): Promise<void> => {
  try {
    console.log(`🔄 adminService: Öğretmen ${isActive ? 'aktif' : 'pasif'} ediliyor:`, teacherId);
    
    const teacherRef = doc(db, 'users', teacherId);
    await updateDoc(teacherRef, {
      isActive,
      updatedAt: Timestamp.now()
    });
    
    console.log('✅ adminService: Öğretmen statusu güncellendi');
  } catch (error) {
    console.error('❌ adminService: Öğretmen statusu güncellenirken hata:', error);
    throw error;
  }
};

// Öğretmen telefon numarasını güncelle
export const updateTeacherPhoneNumber = async (teacherId: string, phoneNumber: string): Promise<void> => {
  try {
    console.log(`📱 adminService: Öğretmen telefon numarası güncelleniyor:`, teacherId);
    
    const teacherRef = doc(db, 'users', teacherId);
    await updateDoc(teacherRef, {
      phoneNumber: phoneNumber.trim(),
      updatedAt: Timestamp.now()
    });
    
    console.log('✅ adminService: Öğretmen telefon numarası güncellendi');
  } catch (error) {
    console.error('❌ adminService: Öğretmen telefon numarası güncellenirken hata:', error);
    throw error;
  }
};

// Tüm atamaları getir - OPTIMIZE EDİLDİ (N+1 problemi çözüldü)
export const getAllAssignments = async (collectionNameFilter?: string): Promise<YoklaAssignment[]> => {
  try {
    console.log('📚 adminService: Tüm atamalar getiriliyor... Collection filter:', collectionNameFilter);
    
    const assignmentsRef = collection(db, 'yoxla-s');
    const querySnapshot = await getDocs(assignmentsRef);
    
    console.log('📊 adminService: Toplam atama sayısı (filtresiz):', querySnapshot.docs.length);
    
    // OPTIMIZASYON: Tüm öğrencileri BIR kere çek
    // Collection filter varsa o collection'dan, yoksa default collection'dan öğrencileri çek
    const targetCollection = collectionNameFilter || 'scanned2610';
    console.log('🚀 adminService: Tüm öğrenciler tek sorguda çekiliyor... Collection:', targetCollection);
    const studentsRef = collection(db, targetCollection);
    const allStudentsSnapshot = await getDocs(studentsRef);
    
    // Öğrencileri memory'de cache'le
    // Buraxılış için variant bazlı, blok için specialtyGroup bazlı cache
    const studentsByKey = new Map<string, any[]>();
    const studentsByVariant = new Map<string, any[]>(); // Buraxılış için
    
    allStudentsSnapshot.forEach((doc) => {
      const data = doc.data();
      const grade = normalizeGrade(pickStudentField(data, 'grade', 'sinif', 'class'));
      const specialtyGroup = pickStudentField(data, 'specialtyGroup', 'qrup');
      const variant = pickStudentField(data, 'variant');
      const normalizedStudent = {
        ...data,
        ...collectQuestionFields(data),
        grade,
        specialtyGroup: specialtyGroup || data.specialtyGroup,
        variant: variant || data.variant,
        xdil: pickStudentField(data, 'xdil', 'xarici_dil', 'foreignLanguage') || data.xdil,
        student_id: pickStudentField(data, 'student_id', 'studentId', 'sagird_id') || data.student_id
      };
      
      // Blok üçün specialtyGroup bazlı cache
      const key = `${grade}-${normalizedStudent.specialtyGroup}`;
      if (!studentsByKey.has(key)) {
        studentsByKey.set(key, []);
      }
      studentsByKey.get(key)!.push(normalizedStudent);
      
      // Buraxılış üçün variant bazlı cache
      if (normalizedStudent.variant) {
        const variantKey = `${grade}-${normalizedStudent.variant}`;
        if (!studentsByVariant.has(variantKey)) {
          studentsByVariant.set(variantKey, []);
          console.log(`🔑 adminService: Buraxılış cache key oluşturuldu: ${variantKey}`);
        }
        studentsByVariant.get(variantKey)!.push(normalizedStudent);
      }
    });
    
    // Debug: Variant cache içeriğini göster
    console.log('🗂️ adminService: Buraxılış variant cache keys:', Array.from(studentsByVariant.keys()));
    studentsByVariant.forEach((students, key) => {
      console.log(`   ${key}: ${students.length} öğrenci`);
    });
    
    console.log('✅ adminService: Öğrenci cache oluşturuldu:', studentsByKey.size, 'farklı grup (blok),', studentsByVariant.size, 'farklı variant (buraxılış)');
    
    const assignments: YoklaAssignment[] = [];
    
    // Şimdi her atama için cache'den hesapla (network isteği yok!)
    querySnapshot.forEach((assignmentDoc) => {
      try {
        const data = assignmentDoc.data();
        const examType = data.examType || 'Blok';
        const collectionName = data.collectionName || 'scanned2610';
        
        // FILTRELEME: Eğer collectionNameFilter verilmişse, sadece o collection'a ait atamaları al
        if (collectionNameFilter) {
          // Eğer atamada collectionName yoksa, bu eski blok imtahanına ait demektir
          const assignmentCollection = collectionName;
          if (assignmentCollection !== collectionNameFilter) {
            console.log(`⏩ adminService: Atama atlandı (farklı collection): ${assignmentDoc.id} - ${assignmentCollection} !== ${collectionNameFilter}`);
            return; // Bu atamayı dahil etme
          }
        }
        
        const qrup = data.qrup || data.blok; // Backward compatibility
        const sinif = normalizeGrade(data.sinif || data.grade); // sinif ve grade aynı şey, her ikisini de kontrol et
        const questionNumber = data.questionNumber;
        const buildingName = data.buildingName;
        const variant = data.variant; // Buraxılış variant (A, B, C, D)
        
        // Cache'den öğrencileri al
        // Buraxılış: variant bazlı, Blok: specialtyGroup bazlı
        let students: any[] = [];
        const isBuraxilis = examType.toLowerCase() === 'buraxılış' || examType.toLowerCase() === 'buraxilis';
        const foreignLanguageSubjects = ['İngilis dili', 'Fransız dili', 'Alman dili', 'Rus dili'];
        
        if (isBuraxilis && variant) {
          const variantKey = `${sinif}-${variant}`;
          let variantStudents = studentsByVariant.get(variantKey) || [];
          console.log(`📊 Buraxılış assignment ${assignmentDoc.id}:`);
          console.log(`   Fenn: ${data.fenn}, Sinif: ${sinif}, Variant: ${variant}`);
          console.log(`   Arama key: ${variantKey}`);
          console.log(`   Bulunan öğrenci (hamısı): ${variantStudents.length}`);

          // Xarici dil fənlərində yalnız uyğun xdil olan şagirdləri saxla (assignment.fenn "Fransız" və ya "Fransız dili" ola bilər)
          const assignmentXdilNorm = normalizeXdil(data.fenn);
          if (foreignLanguageSubjects.includes(assignmentXdilNorm)) {
            variantStudents = variantStudents.filter((s: any) => {
              const studentXdil = normalizeXdil(s.xdil);
              return studentXdil === assignmentXdilNorm;
            });
            console.log(`   🔎 Xdil filtrinden sonra qalan şagird: ${variantStudents.length}`);
          }

          students = variantStudents;

          // Apply studentIdPrefixFilter if present on assignment (Buraxılış only)
          if (data.studentIdPrefixFilter && data.studentIdPrefixFilter.enabled && data.studentIdPrefixFilter.prefixes.length > 0) {
            students = students.filter((s: any) => matchesPrefixFilter(s.student_id, data.studentIdPrefixFilter));
            console.log(`   🔍 Prefix filter: ${data.studentIdPrefixFilter.prefixes.join(', ')} -> ${students.length} students`);
          }

          if (students.length === 0) {
            console.warn(`⚠️ Hiç öğrenci bulunamadı! Available keys:`, Array.from(studentsByVariant.keys()));
          }
        } else {
          const key = `${sinif}-${qrup}`;
          students = studentsByKey.get(key) || [];
        }
        
        const expectedSubGroup = getSubGroupForFenn(data.fenn, qrup);
        
        // DOĞRU MANTIK: Başkaları yokladıklarını çıkar!
        let totalCount = 0;
        let completedCount = 0;
        const questionProgress: { [questionNum: string]: { completed: number; total: number } } = {};
        
        students.forEach((studentData) => {
          // Buraxılış mode: Check variant match instead of subGroup
          let matchesFilter = true;
          if (isBuraxilis && data.variant) {
            matchesFilter = studentData.variant === data.variant;
          } else if (expectedSubGroup) {
            const normalizedExpected = normalizeSubGroup(expectedSubGroup);
            const normalizedStudent = normalizeSubGroup(studentData.subGroup);
            matchesFilter = normalizedExpected === normalizedStudent;
          }
          
          if (matchesFilter) {
            // Buraxılış with questionNumbers: Check if ALL questions are graded
            if (isBuraxilis && data.questionNumbers && Array.isArray(data.questionNumbers)) {
              // Önce kontrol et: Eğer TÜM sorular admin boş kağıt işaretlenmişse, bu öğrenciyi sayma
              const allQuestionsAdminEmpty = data.questionNumbers.every((qNum: string) => {
                const qField = `${qNum}_c`;
                const qValue = studentData[qField];
                const isAdminEmpty = qValue && 
                  typeof qValue === 'object' && 
                  qValue.hocaId === 'admin' && 
                  qValue.note && 
                  qValue.note.includes('Boş sual - Admin tərəfindən təsdiqləndi');
                return isAdminEmpty;
              });
              
              // Eğer TÜM sorular admin boş kağıt işaretlenmişse, bu öğrenciyi bu öğretmene atama olarak gösterme
              if (allQuestionsAdminEmpty) {
                return; // Bu öğrenciyi sayma
              }
              
              // En az bir soru bu öğretmene açık (admin boş kağıt değil), o zaman say
              totalCount++;
              
              // Track per-question completion
              data.questionNumbers.forEach((qNum: string) => {
                const qField = `${qNum}_c`;
                const qValue = studentData[qField];
                
                // Admin boş kağıt kontrolü - bu öğretmene ait değilse total'e dahil etme
                const isAdminEmpty = qValue && 
                  typeof qValue === 'object' && 
                  qValue.hocaId === 'admin' && 
                  qValue.note && 
                  qValue.note.includes('Boş sual - Admin tərəfindən təsdiqləndi');
                
                // Admin boş kağıt işaretlemesi varsa bu soruyu bu öğretmenin assignment'ından çıkar
                if (isAdminEmpty) {
                  return; // Bu soruyu sayma
                }
                
                const isGraded = qValue && typeof qValue === 'object' && qValue.score !== undefined && qValue.score !== null && qValue.score !== '' && qValue.hoca && qValue.hocaId;
                
                if (!questionProgress[qNum]) {
                  questionProgress[qNum] = { completed: 0, total: 0 };
                }
                questionProgress[qNum].total++;
                if (isGraded && qValue.hocaId === data.hocaId) {
                  // Sadece bu öğretmen tarafından yapılan yoklamalar
                  questionProgress[qNum].completed++;
                }
              });
              
              // Check if ALL questions have been graded BY THIS TEACHER (exclude admin empty papers)
              const teacherQuestions = data.questionNumbers.filter((qNum: string) => {
                const qField = `${qNum}_c`;
                const qValue = studentData[qField];
                
                // Admin boş kağıt kontrolü
                const isAdminEmpty = qValue && 
                  typeof qValue === 'object' && 
                  qValue.hocaId === 'admin' && 
                  qValue.note && 
                  qValue.note.includes('Boş sual - Admin tərəfindən təsdiqləndi');
                
                return !isAdminEmpty; // Admin boş kağıtları hariç tut
              });
              
              const allQuestionsGraded = teacherQuestions.every((qNum: string) => {
                const qField = `${qNum}_c`;
                const qValue = studentData[qField];
                // Check if graded by this teacher
                return qValue && typeof qValue === 'object' && qValue.hocaId === data.hocaId;
              });
              
              if (allQuestionsGraded && teacherQuestions.length > 0) {
                completedCount++;
              }
            } else {
              // Blok mode: Match Teacher Dashboard AssignmentList logic exactly
              if (data.questionNumbers && Array.isArray(data.questionNumbers) && data.questionNumbers.length > 0) {
                // DEBUG: Log for Coğrafiya assignment
                const isDebugAssignment = data.fenn === 'Coğrafiya' && sinif === '11' && qrup === 'III';

                // For each question, count only if it's available for this teacher
                // (not graded by admin or other teachers)
                data.questionNumbers.forEach((qNum: string) => {
                  const qField = `${qNum}_c`;
                  const qValue = (studentData as any)[qField];

                  // Initialize question progress
                  if (!questionProgress[qNum]) {
                    questionProgress[qNum] = { completed: 0, total: 0 };
                  }

                  // Check if question is graded by Admin or another teacher
                  const isGradedByOthers = qValue &&
                    typeof qValue === 'object' &&
                    qValue.hocaId &&
                    qValue.hocaId !== data.hocaId;

                  // DEBUG: Log question details
                  if (isDebugAssignment) {
                    console.log(`📊 Question ${qNum}:`, {
                      studentId: (studentData as any).id || (studentData as any).tələbəId,
                      subGroup: (studentData as any).subGroup,
                      qValue: qValue,
                      hocaId: qValue?.hocaId,
                      isGradedByOthers,
                      teacherId: data.hocaId
                    });
                  }

                  // If graded by others (including Admin), don't count this question
                  if (isGradedByOthers) {
                    return; // Don't count this question for this teacher
                  }

                  // Count this question for this teacher only if:
                  // 1. No one has graded it yet (available for this teacher)
                  // 2. This teacher has graded it (completed by this teacher)
                  if (!qValue) {
                    // No one has graded it - this teacher can grade it
                    questionProgress[qNum].total++;
                    if (isDebugAssignment) {
                      console.log(`✅ Question ${qNum}: Available (no grader), total++`);
                    }
                  } else if (qValue && typeof qValue === 'object' && qValue.hocaId === data.hocaId) {
                    // This teacher has graded it
                    questionProgress[qNum].total++;
                    questionProgress[qNum].completed++;
                    if (isDebugAssignment) {
                      console.log(`✅ Question ${qNum}: Graded by this teacher, total++, completed++`);
                    }
                  }
                  // else: admin or other teacher has graded it - don't count for this teacher
                });
              } else {
                // Fallback for old Blok assignments without questionNumbers
                const fennData = studentData[data.fenn];
                const hasGrading = fennData && fennData.hocaId && fennData.hocaId.trim() !== '';
                const gradedByThisTeacher = hasGrading && fennData.hocaId === data.hocaId;
                const gradedByOthers = hasGrading && fennData.hocaId !== data.hocaId;

                // SADECE başkaları yoklamamışsa say (bu hoca yoklamış olsa bile!)
                if (!gradedByOthers) {
                  totalCount++;
                }

                // Completed: Sadece bu hoca yoklamışsa
                if (gradedByThisTeacher) {
                  completedCount++;
                }
              }
            }
          }
        });

        // For Blok with questionNumbers, calculate totalCount and completedCount from questionProgress
        // totalCount = sum of all question totals (not student count!)
        // completedCount = sum of all question completed counts
        if (data.examType === 'Blok' && data.questionNumbers && Array.isArray(data.questionNumbers) && data.questionNumbers.length > 0) {
          totalCount = Object.values(questionProgress).reduce((sum, qp) => sum + qp.total, 0);
          completedCount = Object.values(questionProgress).reduce((sum, qp) => sum + qp.completed, 0);
        }

        // DEBUG - Final
        if (data.fenn === 'Kimya' && sinif === '11' && qrup === 'IV') {
          console.log(`🎯 FINAL Kimya 11-IV: totalCount=${totalCount}, teacherId=${data.hocaId}`);
        }

        // DEBUG - Coğrafiya assignment
        if (data.fenn === 'Coğrafiya' && sinif === '11' && qrup === 'III') {
          console.log(`🎯 FINAL Coğrafiya 11-III:`, {
            teacherId: data.hocaId,
            teacherName: data.hocaName,
            expectedSubGroup,
            questionProgress,
            totalCount,
            completedCount,
            finalTotalCount: totalCount
          });
        }
        
        // PASİF ATAMALAR: totalCount = completedCount (yokladığı kadar!)
        const status = data.status || 'active';
        const finalTotalCount = status === 'passive' ? completedCount : totalCount;
        
        assignments.push({
          id: assignmentDoc.id,
          hocaId: data.hocaId,
          hocaName: data.hocaName,
          fenn: data.fenn,
          sinif: sinif,
          qrup: qrup,
          variant: data.variant, // Buraxılış variant field
          questionNumbers: data.questionNumbers, // Array of question numbers for buraxılış
          questionProgress: Object.keys(questionProgress).length > 0 ? questionProgress : undefined, // Per-question progress for buraxılış
          subGroup: data.subGroup,
          status: status,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          completedCount: completedCount,
          totalCount: finalTotalCount,
          collectionName: collectionName,
          examType: examType,
          questionNumber: questionNumber, // Deprecated, kept for backward compatibility
          buildingName: buildingName,
          // Pool assignment fields
          assignmentType: data.assignmentType || 'individual',
          poolConfig: data.poolConfig,
          // Calculate poolStats for pool assignments
          poolStats: data.assignmentType === 'pool' && data.poolConfig
            ? calculatePoolStats(data.poolConfig, students, data.questionNumbers || [])
            : undefined,
          studentIdPrefixFilter: data.studentIdPrefixFilter
        });
      } catch (assignmentError) {
        console.error('❌ adminService: Atama işlenirken hata:', assignmentDoc.id, assignmentError);
        // Hata olsa bile diğer atamaları yüklemeye devam et
      }
    });
    
    // Admin'in boş kağıt işaretlemelerini de ekle
    // SADECE collection filter yoksa veya blok imtahanıysa admin kayıtlarını göster
    // Buraxılışda admin boş kağız qeydi olmur!
    const adminAssignments = (!collectionNameFilter || collectionNameFilter === 'scanned2610') 
      ? await getAdminEmptyPaperAssignments(allStudentsSnapshot)
      : [];
    assignments.push(...adminAssignments);
    
    console.log('✅ adminService: Atamalar başarıyla getirildi:', assignments.length, '(Admin atamaları:', adminAssignments.length, ')');
    return assignments;
  } catch (error) {
    console.error('❌ adminService: Atamalar getirilirken hata:', error);
    throw error;
  }
};

// Admin'in boş kağıt işaretlemelerini sanal atamalar olarak döndür
const getAdminEmptyPaperAssignments = async (allStudentsSnapshot: any): Promise<YoklaAssignment[]> => {
  const adminAssignments: YoklaAssignment[] = [];
  const adminGradingsByKey = new Map<string, { studentIds: Set<string>, fennler: Set<string> }>();
  
  // Admin tarafından yoklanmış öğrencileri grupla
  allStudentsSnapshot.forEach((doc: any) => {
    const studentData = doc.data();
    const sinif = normalizeGrade(studentData.grade);
    const qrup = studentData.specialtyGroup;
    
    // Tüm fennleri kontrol et
    Object.keys(studentData).forEach(key => {
      const value = studentData[key];
      if (value && typeof value === 'object' && value.hocaId === 'admin') {
        const fenn = key;
        const mapKey = `${sinif}-${qrup}-${fenn}`;
        
        if (!adminGradingsByKey.has(mapKey)) {
          adminGradingsByKey.set(mapKey, { studentIds: new Set(), fennler: new Set() });
        }
        
        const group = adminGradingsByKey.get(mapKey)!;
        // Öğrenciyi unique ID ile ekle (duplicate önlenir)
        group.studentIds.add(studentData.student_id || doc.id);
        group.fennler.add(fenn);
      }
    });
  });
  
  // Her grup için sanal atama oluştur
  adminGradingsByKey.forEach((group, mapKey) => {
    const [sinif, qrup, fenn] = mapKey.split('-');
    const expectedSubGroup = getSubGroupForFenn(fenn, qrup);
    
    // Bu fenn için toplam öğrenci sayısını hesapla (AYNI mantıkla hoca atamaları gibi)
    let totalCount = 0;
    allStudentsSnapshot.forEach((doc: any) => {
      const studentData = doc.data();
      const normalizedStudentGrade = normalizeGrade(studentData.grade);
      if (normalizedStudentGrade === sinif && studentData.specialtyGroup === qrup) {
        // SubGroup kontrolü: fenn için subGroup gerekiyorsa sadece o subGroup, değilse tümü
        let matchesSubGroup = true;
        if (expectedSubGroup) {
          const normalizedExpected = normalizeSubGroup(expectedSubGroup);
          const normalizedStudent = normalizeSubGroup(studentData.subGroup);
          matchesSubGroup = normalizedExpected === normalizedStudent;
        }
        
        if (matchesSubGroup) {
          totalCount++;
        }
      }
    });
    
    adminAssignments.push({
      id: `admin-${mapKey}`, // Unique ID
      hocaId: 'admin',
      hocaName: 'Admin (Boş Kağız)',
      fenn,
      sinif,
      qrup,
      status: 'completed', // Admin işaretlemeleri tamamlanmış sayılır
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      completedCount: group.studentIds.size, // Set size kullan (unique count)
      totalCount: totalCount
    });
  });
  
  return adminAssignments;
};

// Belirli bir öğretmenin atamalarını getir
export const getAssignmentsByTeacherId = async (teacherId: string): Promise<YoklaAssignment[]> => {
  try {
    console.log('📚 adminService: Öğretmen atamaları getiriliyor:', teacherId);
    
    const assignmentsRef = collection(db, 'yoxla-s');
    const q = query(assignmentsRef, where('hocaId', '==', teacherId));
    const querySnapshot = await getDocs(q);
    
    // OPTIMIZASYON: Tüm öğrencileri BIR kere çek
    const studentsRef = collection(db, 'scanned2610');
    const allStudentsSnapshot = await getDocs(studentsRef);
    
    // Öğrencileri memory'de cache'le
    const studentsByKey = new Map<string, any[]>();
    allStudentsSnapshot.forEach((doc) => {
      const data = doc.data();
      const grade = normalizeGrade(data.grade);
      const key = `${grade}-${data.specialtyGroup}`;
      if (!studentsByKey.has(key)) {
        studentsByKey.set(key, []);
      }
      studentsByKey.get(key)!.push(data);
    });
    
    const assignments: YoklaAssignment[] = [];
    
    // Her atama için cache'den hesapla (network isteği yok!)
    querySnapshot.forEach((assignmentDoc) => {
      const data = assignmentDoc.data();
      const qrup = data.qrup || data.blok; // Backward compatibility
      const sinif = normalizeGrade(data.sinif || data.grade); // sinif ve grade aynı şey, her ikisini de kontrol et
      
      // Cache'den öğrencileri al
      const key = `${sinif}-${qrup}`;
      const students = studentsByKey.get(key) || [];
      
        const expectedSubGroup = getSubGroupForFenn(data.fenn, qrup);
        
        // DOĞRU MANTIK: Başkaları yokladıklarını çıkar!
        let totalCount = 0;
        let completedCount = 0;
        
        students.forEach((studentData) => {
          let matchesSubGroup = true;
          if (expectedSubGroup) {
            const normalizedExpected = normalizeSubGroup(expectedSubGroup);
            const normalizedStudent = normalizeSubGroup(studentData.subGroup);
            matchesSubGroup = normalizedExpected === normalizedStudent;
          }
          
          if (matchesSubGroup) {
            const fennData = studentData[data.fenn];
            const hasGrading = fennData && fennData.hocaId && fennData.hocaId.trim() !== '';
            const gradedByThisTeacher = hasGrading && fennData.hocaId === data.hocaId;
            const gradedByOthers = hasGrading && fennData.hocaId !== data.hocaId;
            
            // SADECE başkaları yoklamamışsa say (bu hoca yoklamış olsa bile!)
            if (!gradedByOthers) {
              totalCount++;
            }
            
            // Completed: Sadece bu hoca yoklamışsa
            if (gradedByThisTeacher) {
              completedCount++;
            }
          }
        });
      
      // PASİF ATAMALAR: totalCount = completedCount (yokladığı kadar!)
      const status = data.status || 'active';
      const finalTotalCount = status === 'passive' ? completedCount : totalCount;
      
      assignments.push({
        id: assignmentDoc.id,
        hocaId: data.hocaId,
        hocaName: data.hocaName,
        fenn: data.fenn,
        sinif: sinif,
        qrup: qrup,
        subGroup: data.subGroup,
        status: status,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        completedCount: completedCount,
        totalCount: finalTotalCount
      });
    });
    
    console.log('✅ adminService: Öğretmen atamaları getirildi:', assignments.length);
    return assignments;
  } catch (error) {
    console.error('❌ adminService: Öğretmen atamaları getirilirken hata:', error);
    throw error;
  }
};

// Helper function to determine question numbers for Blok exams from fennConfig
// Blok exams use the last 3 questions of each fenn range (end-2, end-1, end)
const getBlokQuestionNumbers = (fenn: string, qrup: string): string[] => {
  const qrupConfig = QRUP_CONFIGS.find(q => q.qrup === qrup);
  const fennRange = qrupConfig?.fennler.find(f => f.fenn === fenn);

  if (!fennRange) {
    console.warn('Fenn aralığı bulunamadı:', fenn, qrup, ' - Varsayılan [28, 29, 30] kullanılıyor');
    return ['Q28', 'Q29', 'Q30'];
  }

  // Return the last 3 questions: end-2, end-1, end
  const questionNumbers = [
    `Q${fennRange.end - 2}`,
    `Q${fennRange.end - 1}`,
    `Q${fennRange.end}`
  ];

  console.log(`📚 Blok soru numaraları belirlendi: ${fenn} (${qrup}) → ${questionNumbers.join(', ')}`);
  return questionNumbers;
};

// Atama oluştur - Duplicate kontrolü ile
export const createAssignment = async (
  teacherId: string,
  teacherName: string,
  fenn: string,
  sinif: string,
  qrupOrVariant: string,
  collectionName: string = 'scanned2610',
  examType: 'Blok' | 'buraxilis' = 'Blok',
  buildingName?: string,
  questionNumbers?: string[],
  studentIdPrefixFilter?: StudentIdPrefixFilter
): Promise<{ success: boolean; message: string; assignmentId?: string }> => {
  try {
    console.log('➕ adminService: Atama oluşturuluyor:', { teacherId, fenn, sinif, qrupOrVariant, examType, buildingName, questionNumbers });

    // For buraxilis, create ONE assignment with array of questionNumbers
    if (examType === 'buraxilis' && questionNumbers && questionNumbers.length > 0) {
      const assignmentsRef = collection(db, 'yoxla-s');

      // Sual səviyyəli toqquşma: eyni sinif/variant/fənn üçün bütün aktiv təyinatları götür, suallar kəsişməməlidir
      const q = query(
        assignmentsRef,
        where('sinif', '==', sinif),
        where('variant', '==', qrupOrVariant),
        where('fenn', '==', fenn),
        where('collectionName', '==', collectionName),
        where('status', '==', 'active')
      );

      const existingSnapshot = await getDocs(q);
      const existingAssignments: YoklaAssignment[] = existingSnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        hocaId: d.data().hocaId,
        fenn: d.data().fenn,
        sinif: d.data().sinif,
        qrup: d.data().qrup,
        variant: d.data().variant,
        questionNumbers: d.data().questionNumbers,
        questionNumber: d.data().questionNumber,
        hocaName: d.data().hocaName,
      })) as YoklaAssignment[];

      const collision = checkBuraxilisQuestionCollision(existingAssignments, questionNumbers, studentIdPrefixFilter);
      if (collision.hasCollision && collision.teacherName != null) {
        const conflictList = collision.conflictingQuestions?.join(', ') ?? '';
        const message = conflictList
          ? `${conflictList} artıq ${collision.teacherName} tərəfindən təyin edilib. Toqquşma yoxlanılmalıdır.`
          : `Bu təyinat artıq mövcuddur: Sinif ${sinif}, Variant ${qrupOrVariant}, Fənn ${fenn} (Müəllim: ${collision.teacherName})`;
        console.log('⚠️ adminService: Toqquşma:', message);
        return { success: false, message };
      }
      
      // Öğrenci sayısını hesapla
      const studentsRef = collection(db, collectionName);
      const normalizedSinif = normalizeGrade(sinif);
      const numericSinifCreate = Number(normalizedSinif);

      const createBuildingConstraints = (buildingName && buildingName !== 'Hamısı')
        ? [where('buildingName', '==', buildingName)]
        : [];

      const createStringQuery = query(
        studentsRef,
        where('grade', '==', normalizedSinif),
        where('variant', '==', qrupOrVariant),
        ...createBuildingConstraints
      );
      const createQueryPromises: Promise<any>[] = [getDocs(createStringQuery)];

      if (!isNaN(numericSinifCreate)) {
        const createNumericQuery = query(
          studentsRef,
          where('grade', '==', numericSinifCreate),
          where('variant', '==', qrupOrVariant),
          ...createBuildingConstraints
        );
        createQueryPromises.push(getDocs(createNumericQuery));
      }

      const createQuerySnapshots = await Promise.all(createQueryPromises);

      const createUniqueDocMap = new Map<string, any>();
      createQuerySnapshots.forEach(snapshot => {
        snapshot.docs.forEach((d: any) => {
          if (!createUniqueDocMap.has(d.id)) createUniqueDocMap.set(d.id, d);
        });
      });

      // Convert to snapshot-like structure for downstream code compatibility
      const studentsSnapshot = { docs: Array.from(createUniqueDocMap.values()) };

      // Apply prefix filter if enabled (before xdil and totalStudents calculation)
      let filteredDocs = studentsSnapshot.docs;
      if (studentIdPrefixFilter && studentIdPrefixFilter.enabled && studentIdPrefixFilter.prefixes.length > 0) {
        filteredDocs = filteredDocs.filter((d: any) =>
          matchesPrefixFilter(d.data().student_id, studentIdPrefixFilter)
        );
        console.log(`🔍 Prefix filter applied: ${studentIdPrefixFilter.prefixes.join(', ')} -> ${filteredDocs.length} students`);
      }

      let totalStudents = filteredDocs.length;
      // Xarici dil fənnində yalnız həmin xdil-i olan şagirdləri say (Fransız dili = yalnız xdil Fransız olanlar)
      const fennXdilNorm = normalizeXdil(fenn);
      if (['İngilis dili', 'Fransız dili', 'Alman dili', 'Rus dili'].includes(fennXdilNorm)) {
        totalStudents = filteredDocs.filter((d: any) => normalizeXdil(d.data().xdil) === fennXdilNorm).length;
        console.log(`📊 Buraxılış xarici dil (${fenn}): Xdil filtrindən sonra tələbə sayı: ${totalStudents}`);
      } else {
        console.log(`📊 Buraxılış assignment: Sinif ${sinif}, Variant ${qrupOrVariant}, Toplam öğrenci: ${totalStudents}`);
      }

      // Buraxilis assignment oluştur - TEK ASSIGNMENT, SORU ARRAY İLE
      const assignmentData = {
        hocaId: teacherId,
        hocaName: teacherName,
        fenn,
        sinif,
        variant: qrupOrVariant,
        qrup: qrupOrVariant, // Backward compatibility
        questionNumbers: questionNumbers, // ARRAY olarak kaydet
        collectionName,
        examType: 'buraxilis',
        buildingName: buildingName || 'Hamısı',
        status: 'active',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        completedCount: 0,
        totalCount: totalStudents,
        ...(studentIdPrefixFilter && studentIdPrefixFilter.enabled && studentIdPrefixFilter.prefixes.length > 0
          ? { studentIdPrefixFilter }
          : {})
      };
      
      const docRef = await addDoc(assignmentsRef, assignmentData);
      console.log(`✅ adminService: Buraxılış ataması oluşturuldu:`, docRef.id, 'Sorular:', questionNumbers);
      
      return {
        success: true,
        message: `${questionNumbers.length} sual üçün atama uğurla yaradıldı`,
        assignmentId: docRef.id
      };
    }
    
    // Blok exam: Original logic
    // Duplicate kontrolü - Aynı sınıf, qrup, fenn için AKTIF atama var mı?
    const assignmentsRef = collection(db, 'yoxla-s');
    const q = query(
      assignmentsRef,
      where('sinif', '==', sinif),
      where('qrup', '==', qrupOrVariant),
      where('fenn', '==', fenn),
      where('collectionName', '==', collectionName),
      where('status', '==', 'active') // Sadece aktif atamaları kontrol et
    );
    
    const existingAssignments = await getDocs(q);
    
    if (!existingAssignments.empty) {
      const existingAssignment = existingAssignments.docs[0].data();
      const message = `Bu təyinat artıq mövcuddur: Sinif ${sinif}, Qrup ${qrupOrVariant}, Fənn ${fenn} (Müəllim: ${existingAssignment.hocaName})`;
      console.log('⚠️ adminService: Duplicate atama:', message);
      return { success: false, message };
    }
    
    // Öğrenci sayısını hesapla (scanned collection'dan)
    // Fenn'e göre subGroup'u belirle
    const expectedSubGroup = getSubGroupForFenn(fenn, qrupOrVariant);
    
    // Grade'i normalize et (string'e çevir)
    const normalizedSinif = normalizeGrade(sinif);
    
    // Hem string hem number olarak sorgula (Firestore'da her iki tip de olabilir!)
    const queryPromises: Promise<any>[] = [];
    
    const studentsRef = collection(db, collectionName);
    
    // String olarak sorgula
    const stringQuery = query(
      studentsRef,
      where('grade', '==', normalizedSinif),
      where('specialtyGroup', '==', qrupOrVariant)
    );
    queryPromises.push(getDocs(stringQuery));
    
    // Number olarak da sorgula (eğer number'a çevrilebiliyorsa)
    if (!isNaN(Number(sinif))) {
      const numericSinif = Number(sinif);
      const numberQuery = query(
        studentsRef,
        where('grade', '==', numericSinif),
        where('specialtyGroup', '==', qrupOrVariant)
      );
      queryPromises.push(getDocs(numberQuery));
    }
    
    // Her iki sorguyu paralel çalıştır ve sonuçları birleştir
    const querySnapshots = await Promise.all(queryPromises);
    
    // Tüm sonuçları birleştir (duplicate'leri çıkar - document ID'ye göre)
    const uniqueDocs = new Map<string, QueryDocumentSnapshot<DocumentData>>();
    querySnapshots.forEach(snapshot => {
      snapshot.docs.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
        if (!uniqueDocs.has(doc.id)) {
          uniqueDocs.set(doc.id, doc);
        }
      });
    });
    
    // Map'ten QuerySnapshot benzeri bir yapı oluştur
    const studentsSnapshot = {
      docs: Array.from(uniqueDocs.values()),
      forEach: (callback: (doc: QueryDocumentSnapshot<DocumentData>) => void) => {
        uniqueDocs.forEach((doc) => callback(doc));
      },
      empty: uniqueDocs.size === 0
    } as any;
    let totalStudents = 0;
    
    // SubGroup'a göre filtrele
    studentsSnapshot.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
      const data = doc.data();
      if (expectedSubGroup) {
        // Bu fenn subGroup gerektiriyorsa, sadece o subGroup'lu öğrencileri say
        // SubGroup normalize ederek karşılaştır (RI -> Rİ)
        const normalizedExpected = normalizeSubGroup(expectedSubGroup);
        const normalizedStudent = normalizeSubGroup(data.subGroup);
        if (normalizedExpected === normalizedStudent) {
          totalStudents++;
        }
      } else {
        // Bu fenn subGroup gerektirmiyorsa, TÜM öğrencileri say
        totalStudents++;
      }
    });
    
    // Atama oluştur
    // For Blok exams, determine questionNumbers from fennConfig
    const blokQuestionNumbers = getBlokQuestionNumbers(fenn, qrupOrVariant);

    const assignmentData: any = {
      hocaId: teacherId,
      hocaName: teacherName,
      fenn,
      sinif,
      qrup: qrupOrVariant,
      questionNumbers: blokQuestionNumbers, // Store questionNumbers array for Blok
      collectionName,
      examType,
      status: 'active',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      completedCount: 0, // Will be calculated by getYoxlamaStats
      totalCount: totalStudents
    };

    // Add buildingName if specified
    if (buildingName) {
      assignmentData.buildingName = buildingName;
    }

    const docRef = await addDoc(assignmentsRef, assignmentData);

    console.log('✅ adminService: Blok ataması oluşturuldu:', docRef.id, 'Sorular:', blokQuestionNumbers);
    return {
      success: true,
      message: `Atama uğurla yaradıldı (${blokQuestionNumbers.join(', ')})`,
      assignmentId: docRef.id
    };
  } catch (error) {
    console.error('❌ adminService: Atama oluşturulurken hata:', error);
    throw error;
  }
};

// Migration utility: Update completedCount for existing Blok assignments
export const updateBlokAssignmentsProgress = async (collectionName: string = 'scanned2610'): Promise<{
  success: boolean;
  updated: number;
  message: string;
}> => {
  try {
    console.log('🔄 adminService: Blok assignments progress update started...');

    // Get all Blok assignments
    const assignmentsRef = collection(db, 'yoxla-s');
    const q = query(
      assignmentsRef,
      where('collectionName', '==', collectionName),
      where('examType', '==', 'Blok')
    );

    const snapshot = await getDocs(q);
    console.log(`📋 Found ${snapshot.size} Blok assignments to update...`);

    let updatedCount = 0;

    for (const assignmentDoc of snapshot.docs) {
      const assignment = assignmentDoc.data();
      const assignmentId = assignmentDoc.id;

      console.log(`\n🔄 Processing assignment: ${assignment.hocaName} - ${assignment.fenn} - ${assignment.sinif} ${assignment.qrup}`);

      // Get questionNumbers for this assignment
      const questionNumbers = assignment.questionNumbers || [];
      if (questionNumbers.length === 0) {
        console.log(`⚠️ No questionNumbers found for assignment ${assignmentId}, skipping...`);
        continue;
      }

      // Get students for this assignment
      const studentsRef = collection(db, collectionName);
      const normalizedSinif = normalizeGrade(assignment.sinif);

      const queryPromises: Promise<any>[] = [];

      // String olarak sorgula
      const stringQuery = query(
        studentsRef,
        where('grade', '==', normalizedSinif),
        where('specialtyGroup', '==', assignment.qrup)
      );
      queryPromises.push(getDocs(stringQuery));

      // Number olarak da sorgula
      if (!isNaN(Number(assignment.sinif))) {
        const numericSinif = Number(assignment.sinif);
        const numberQuery = query(
          studentsRef,
          where('grade', '==', numericSinif),
          where('specialtyGroup', '==', assignment.qrup)
        );
        queryPromises.push(getDocs(numberQuery));
      }

      const querySnapshots = await Promise.all(queryPromises);

      // Merge unique documents
      const uniqueDocs = new Map<string, QueryDocumentSnapshot<DocumentData>>();
      querySnapshots.forEach(snapshot => {
        snapshot.docs.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
          if (!uniqueDocs.has(doc.id)) {
            uniqueDocs.set(doc.id, doc);
          }
        });
      });

      const studentsSnapshot = {
        docs: Array.from(uniqueDocs.values()),
        forEach: (callback: (doc: QueryDocumentSnapshot<DocumentData>) => void) => {
          uniqueDocs.forEach((doc) => callback(doc));
        },
        empty: uniqueDocs.size === 0
      } as any;

      if (studentsSnapshot.empty) {
        console.log(`⚠️ No students found for assignment ${assignmentId}`);
        continue;
      }

      // Calculate completedCount from student data
      let completedCount = 0;
      const questionProgress: { [qNum: string]: { total: number; completed: number } } = {};

      // Initialize questionProgress
      questionNumbers.forEach((qNum: string) => {
        questionProgress[qNum] = { total: 0, completed: 0 };
      });

      // Count graded questions
      studentsSnapshot.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
        const data = doc.data();

        questionNumbers.forEach((qNum: string) => {
          const checkedField = `${qNum}_c`;
          const gradingData = (data as any)[checkedField];

          questionProgress[qNum].total++;

          if (gradingData && gradingData.hocaId) {
            questionProgress[qNum].completed++;
          }
        });
      });

      // Calculate total completed
      questionNumbers.forEach((qNum: string) => {
        completedCount += questionProgress[qNum].completed;
      });

      const totalStudents = uniqueDocs.size;
      const totalCount = totalStudents * questionNumbers.length;

      console.log(`📊 Assignment ${assignmentId}:`);
      console.log(`   Total students: ${totalStudents}`);
      console.log(`   Questions: ${questionNumbers.join(', ')}`);
      console.log(`   Completed: ${completedCount}/${totalCount}`);
      console.log(`   Question progress:`, questionProgress);

      // Update assignment
      await updateDoc(doc(db, 'yoxla-s', assignmentDoc.id), {
        completedCount: completedCount,
        totalCount: totalCount,
        questionProgress: questionProgress,
        updatedAt: Timestamp.now()
      });

      updatedCount++;
      console.log(`✅ Updated assignment ${assignmentId}`);
    }

    console.log(`\n✅ Migration complete! Updated ${updatedCount} assignments.`);

    return {
      success: true,
      updated: updatedCount,
      message: `${updatedCount} Blok assignments updated successfully`
    };
  } catch (error) {
    console.error('❌ Migration error:', error);
    return {
      success: false,
      updated: 0,
      message: `Migration failed: ${error}`
    };
  }
};

// Migration utility: Add questionNumbers to existing Blok assignments
export const migrateBlokAssignments = async (): Promise<{
  success: boolean;
  migrated: number;
  failed: number;
  errors: string[];
}> => {
  try {
    console.log('🔄 adminService: Blok assignments migration started...');

    const assignmentsRef = collection(db, 'yoxla-s');
    // Query all Blok assignments
    const q = query(
      assignmentsRef,
      where('examType', '==', 'Blok')
    );

    const snapshot = await getDocs(q);
    const assignments = snapshot.docs;

    console.log(`📋 Found ${assignments.length} Blok assignments to check...`);

    let migrated = 0;
    let failed = 0;
    const errors: string[] = [];

    // Process each assignment
    for (const docSnap of assignments) {
      try {
        const assignment = docSnap.data() as YoklaAssignment;

        // Skip if already has questionNumbers
        if (assignment.questionNumbers && assignment.questionNumbers.length > 0) {
          console.log(`✓ Assignment ${assignment.id} already has questionNumbers, skipping...`);
          continue;
        }

        // Calculate questionNumbers from fenn and qrup
        const questionNumbers = getBlokQuestionNumbers(assignment.fenn, assignment.qrup);

        // Update the assignment
        await updateDoc(doc(db, 'yoxla-s', docSnap.id), {
          questionNumbers,
          updatedAt: Timestamp.now()
        });

        console.log(`✅ Migrated assignment ${assignment.id}: ${assignment.fenn} (${assignment.qrup}) → ${questionNumbers.join(', ')}`);
        migrated++;
      } catch (err: any) {
        console.error(`❌ Failed to migrate assignment ${docSnap.id}:`, err);
        failed++;
        errors.push(`${docSnap.id}: ${err.message}`);
      }
    }

    console.log(`🎉 Migration complete: ${migrated} migrated, ${failed} failed`);

    return {
      success: true,
      migrated,
      failed,
      errors
    };
  } catch (error: any) {
    console.error('❌ adminService: Migration failed:', error);
    return {
      success: false,
      migrated: 0,
      failed: 0,
      errors: [error.message]
    };
  }
};

// Atama statusunu güncelle
export const updateAssignmentStatus = async (
  assignmentId: string,
  status: 'active' | 'completed' | 'passive'
): Promise<void> => {
  try {
    console.log('🔄 adminService: Atama statusu güncelleniyor:', assignmentId, status);
    
    const assignmentRef = doc(db, 'yoxla-s', assignmentId);
    
    // Pasif yapıldığında totalCount = completedCount yapmalıyız
    if (status === 'passive') {
      const assignmentSnap = await getDoc(assignmentRef);
      if (assignmentSnap.exists()) {
        const data = assignmentSnap.data();
        const completedCount = data.completedCount || 0;
        
        await updateDoc(assignmentRef, {
          status,
          totalCount: completedCount, // Pasif olunca totalCount = completedCount
          updatedAt: Timestamp.now()
        });
        
        console.log(`✅ adminService: Pasif yapıldı - totalCount: ${completedCount}/${completedCount}`);
        return;
      }
    }
    
    // Diğer durumlar için sadece status güncelle
    await updateDoc(assignmentRef, {
      status,
      updatedAt: Timestamp.now()
    });
    
    console.log('✅ adminService: Atama statusu güncellendi');
  } catch (error) {
    console.error('❌ adminService: Atama statusu güncellenirken hata:', error);
    throw error;
  }
};

// Atama sil
export const deleteAssignment = async (assignmentId: string): Promise<void> => {
  try {
    console.log('🗑️ adminService: Atama siliniyor:', assignmentId);
    
    const assignmentRef = doc(db, 'yoxla-s', assignmentId);
    await deleteDoc(assignmentRef);
    
    console.log('✅ adminService: Atama silindi');
  } catch (error) {
    console.error('❌ adminService: Atama silinirken hata:', error);
    throw error;
  }
};

// Buraxılış atamasının sual siyahısını yenilə (atamadan sual çıxar)
export const updateBuraxilisAssignmentQuestions = async (
  assignmentId: string,
  newQuestionNumbers: string[]
): Promise<{ success: boolean; message: string }> => {
  try {
    const assignmentRef = doc(db, 'yoxla-s', assignmentId);
    const assignmentSnap = await getDoc(assignmentRef);
    if (!assignmentSnap.exists()) {
      return { success: false, message: 'Təyinat tapılmadı' };
    }
    const data = assignmentSnap.data();
    const examType = data.examType;
    const currentQuestionNumbers: string[] = data.questionNumbers ?? [];
    if (examType !== 'buraxilis') {
      return { success: false, message: 'Yalnız buraxılış təyinatları redaktə edilə bilər' };
    }
    if (!Array.isArray(currentQuestionNumbers) || currentQuestionNumbers.length === 0) {
      return { success: false, message: 'Təyinatda sual siyahısı yoxdur' };
    }
    const currentSet = new Set(currentQuestionNumbers);
    const validNew = newQuestionNumbers.filter((q) => currentSet.has(q));
    if (validNew.length !== newQuestionNumbers.length) {
      return { success: false, message: 'Yeni siyahıda təyinatda olmayan suallar var' };
    }
    if (validNew.length === 0) {
      return { success: false, message: 'Ən azı bir sual qalmalıdır' };
    }
    const questionProgress: { [q: string]: { completed: number; total: number } } =
      data.questionProgress ?? {};
    const filteredProgress: { [q: string]: { completed: number; total: number } } = {};
    let totalCount = 0;
    let completedCount = 0;
    for (const q of validNew) {
      const qp = questionProgress[q];
      if (qp) {
        filteredProgress[q] = qp;
        totalCount += qp.total;
        completedCount += qp.completed;
      }
    }
    await updateDoc(assignmentRef, {
      questionNumbers: validNew,
      questionProgress: Object.keys(filteredProgress).length > 0 ? filteredProgress : {},
      totalCount,
      completedCount,
      updatedAt: Timestamp.now()
    });
    console.log(`✅ adminService: Buraxılış təyinatı yeniləndi: ${assignmentId}, suallar: ${validNew.join(', ')}`);
    return {
      success: true,
      message: `Təyinat yeniləndi: ${validNew.length} sual qaldı, ${currentQuestionNumbers.length - validNew.length} sual atamadan çıxarıldı`
    };
  } catch (error) {
    console.error('❌ adminService: Buraxılış təyinatı yenilənərkən xəta:', error);
    throw error;
  }
};

// Toplam yoklama istatistikleri
export interface YoxlamaStats {
  totalScannedData: number; // Sistemdeki toplam yoklama verisi
  totalAssignments: number; // Toplam tayin edilmiş atama
  activeAssignments: number; // Aktif atamalar
  completedAssignments: number; // Tamamlanmış atamalar
  passiveAssignments: number; // Pasif atamalar
  totalTeachers: number; // Toplam öğretmen
  activeTeachers: number; // Aktif öğretmen
  totalCompleted: number; // Tamamlanmış yoklama sayısı
  totalRemaining: number; // Kalan yoklama sayısı
  adminEmptyPapers?: number; // Admin boş kağıt işaretlemeleri sayısı
  // Fenn bazlı toplam (aktif atamaların toplamı)
  totalFennBased: {
    completed: number;
    total: number;
    percentage: number;
  };
  // Sistem kapasitesi bazlı (427 × 3 = 1281)
  totalSystemBased: {
    completed: number;
    total: number;
    percentage: number;
  };
  // Sınıf ve qrup bazlı istatistikler
  byClass: {
    [sinif: string]: {
      total: number;
      completed: number;
      remaining: number;
      byQrup: {
        [qrup: string]: {
          total: number;
          completed: number;
          remaining: number;
          byFenn?: {
            [fenn: string]: {
              total: number;
              completed: number;
              remaining: number;
            };
          };
        };
      };
    };
  };
}

// İstatistikleri getir
export const getYoxlamaStats = async (collectionName: string = 'scanned2610', examType: 'blok' | 'buraxilis' = 'blok'): Promise<YoxlamaStats> => {
  try {
    console.log('📊 adminService: İstatistikler hesaplanıyor... Collection:', collectionName, 'Type:', examType);
    
    // Buraxılış imtahanı üçün fərqli statistika
    if (examType === 'buraxilis') {
      return await getBuraxilisStats(collectionName);
    }
    
    // OPTIMIZASYON: Tüm query'leri PARALEL çalıştır
    // ÖNEMLİ: getAllAssignments'a collectionName filter'ı gönder!
    const [scannedSnapshot, assignments, teachers] = await Promise.all([
      getDocs(collection(db, collectionName)),
      getAllAssignments(collectionName),
      getAllTeachers()
    ]);

    const totalScannedData = scannedSnapshot.size;

    // ⚡ BLOK İÇİN COMPLETEDCOUNT VE QUESTIONPROGRESS HESAPLA
    // Blok assignment'ların değerlerini student data'dan hesapla (Buraxılış mantığı gibi)
    const studentMap = new Map<string, any>();
    scannedSnapshot.forEach(doc => {
      studentMap.set(doc.id, doc.data());
    });

    const blokAssignments = assignments.filter(a => a.examType === 'Blok');

    // Her Blok assignment için completedCount ve questionProgress hesapla
    for (const assignment of blokAssignments) {
      const questionNumbers = assignment.questionNumbers || [];
      if (questionNumbers.length === 0) continue;

      let gradedCount = 0;
      const questionProgress: { [qNum: string]: { total: number; completed: number } } = {};

      // Initialize questionProgress
      questionNumbers.forEach(qNum => {
        questionProgress[qNum] = { total: 0, completed: 0 };
      });

      // Student data'dan hesapla
      studentMap.forEach((student) => {
        // Match grade
        const studentGrade = normalizeGrade(student.grade);
        const assignmentGrade = normalizeGrade(assignment.sinif);
        if (studentGrade !== assignmentGrade) return;

        // Match qrup
        if (student.specialtyGroup !== assignment.qrup) return;

        // Match subGroup if required
        const expectedSubGroup = getSubGroupForFenn(assignment.fenn, assignment.qrup);
        if (expectedSubGroup) {
          const normalizedExpected = normalizeSubGroup(expectedSubGroup);
          const normalizedStudent = normalizeSubGroup(student.subGroup);
          if (normalizedExpected !== normalizedStudent) return;
        }

        // Önce kontrol et: Eğer TÜM sorular admin boş kağıt işaretlenmişse, bu öğrenciyi sayma
        const allQuestionsAdminEmpty = questionNumbers.every((qNum: string) => {
          const checkedField = `${qNum}_c`;
          const gradingData = (student as any)[checkedField];
          const isAdminEmpty = gradingData && 
            typeof gradingData === 'object' && 
            gradingData.hocaId === 'admin' && 
            gradingData.note && 
            gradingData.note.includes('Boş sual - Admin tərəfindən təsdiqləndi');
          return isAdminEmpty;
        });
        
        // Eğer TÜM sorular admin boş kağıt işaretlenmişse, bu öğrenciyi bu öğretmene atama olarak gösterme
        if (allQuestionsAdminEmpty) {
          return; // Bu öğrenciyi sayma
        }

        // Student matched - count questions
        questionNumbers.forEach(qNum => {
          const checkedField = `${qNum}_c`;
          const gradingData = (student as any)[checkedField];

          // Admin boş kağıt kontrolü - bu öğretmene ait değilse total'e dahil etme
          const isAdminEmpty = gradingData && 
            typeof gradingData === 'object' && 
            gradingData.hocaId === 'admin' && 
            gradingData.note && 
            gradingData.note.includes('Boş sual - Admin tərəfindən təsdiqləndi');
          
          // Admin boş kağıt işaretlemesi varsa bu soruyu bu öğretmenin assignment'ından çıkar
          if (isAdminEmpty) {
            return; // Bu soruyu sayma
          }

          questionProgress[qNum].total++;

          if (gradingData && gradingData.hocaId) {
            questionProgress[qNum].completed++;
            gradedCount++;
          }
        });
      });

      // Update assignment object
      assignment.completedCount = gradedCount;
      // totalCount: questionProgress total'larının toplamı (Buraxılış gibi)
      assignment.totalCount = Object.values(questionProgress).reduce((sum, q) => sum + q.total, 0);
      assignment.questionProgress = questionProgress;
    }
    
    const totalAssignments = assignments.length;
    const activeAssignments = assignments.filter(a => a.status === 'active').length;
    const completedAssignments = assignments.filter(a => a.status === 'completed').length;
    const passiveAssignments = assignments.filter(a => a.status === 'passive').length;
    
    const totalTeachers = teachers.length;
    const activeTeachers = teachers.filter(t => t.isActive !== false).length;
    
    // SİSTEM KAPASİTESİ (Admin + Teacher için)
    // Blok için: Her öğrenci 9 soru var (Q28, Q29, Q30, Q58, Q59, Q60, Q88, Q89, Q90)
    const totalStudentsInSystem = scannedSnapshot.size;
    const blokQuestionsPerStudent = 9;
    const totalSystemCapacity = totalStudentsInSystem * blokQuestionsPerStudent;

    // FENN BAZLI TOPLAM (SADECE ÖĞRETMEN YOKLAMALARI - ADMİN HARİÇ!)
    // Blok için de Buraxılış gibi questionProgress kullanarak soru bazlı hesapla
    let fennBasedTotal = 0;
    let fennBasedCompleted = 0;
    assignments.forEach(a => {
      // Admin yoklamalarını sayma!
      if (a.hocaId === 'admin') return;

      // Blok'ta da questionProgress kullanarak soru bazlı hesapla (Buraxılış mantığı)
      if (a.questionProgress && Object.keys(a.questionProgress).length > 0) {
        // Her soru için total ve completed'i topla
        Object.values(a.questionProgress).forEach((qProgress: any) => {
          const qTotal = qProgress.total || 0;
          const qCompleted = qProgress.completed || 0;

          if (a.status === 'active') {
            // Aktif atama: Normal total
            fennBasedTotal += qTotal;
          } else if (a.status === 'passive') {
            // Pasif atama: Sadece completed kadar say
            fennBasedTotal += qCompleted;
          }

          fennBasedCompleted += qCompleted;
        });
      } else {
        // Eğer questionProgress yoksa, eski mantık
        fennBasedCompleted += a.completedCount || 0;

        if (a.status === 'active') {
          // Aktif atama: Normal totalCount
          fennBasedTotal += a.totalCount || 0;
        } else if (a.status === 'passive') {
          // Pasif atama: totalCount = completedCount (yokladığı kadar!)
          fennBasedTotal += a.completedCount || 0;
        }
      }
    });

    // SİSTEM KAPASİTESİ BAZLI (Admin + Teacher için) + ADMIN BOŞ KAĞIT SAYIMI
    // TÜM yoklamaları say (admin + teacher)
    let systemCompleted = 0;
    let adminEmptyPapers = 0; // Admin boş kağıt sayısı

    // Scanned data'dan direkt say (Blok için double_check_empty kontrolü)
    scannedSnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();

      // Blok sorularını kontrol et
      const blokQuestions = ['Q28', 'Q29', 'Q30', 'Q58', 'Q59', 'Q60', 'Q88', 'Q89', 'Q90'];

      blokQuestions.forEach(q => {
        const qField = `${q}_c`;
        const qValue = data[qField];

        if (qValue && typeof qValue === 'object') {
          systemCompleted++;
        }

        // Admin boş kağıt kontrolü - Q## field'ında kontrol et (Q##_c DEĞİL!)
        const qData = data[q]; // Q28, Q29, etc. field'ı
        if (qData && typeof qData === 'object') {
          // Only double_check_empty === true cases count (verified empty papers)
          if (qData.double_check_empty === true) {
            adminEmptyPapers++;
          }
        }
      });
    });

    console.log(`🔍 Blok admin empty papers found: ${adminEmptyPapers}`);

    const systemRemaining = totalSystemCapacity - systemCompleted;
    
    // Sınıf ve qrup bazlı istatistikler
    const byClass: YoxlamaStats['byClass'] = {};
    
    // İlk önce sınıf ve qrup yapılarını oluştur ve ÖĞRENCİ SAYISINI hesapla
    const studentCountByClassQrup = new Map<string, number>();
    
    scannedSnapshot.forEach((doc) => {
      const data = doc.data();
      const sinif = normalizeGrade(data.grade);
      const qrup = data.specialtyGroup;
      const key = `${sinif}-${qrup}`;
      
      // Öğrenci sayısını say
      studentCountByClassQrup.set(key, (studentCountByClassQrup.get(key) || 0) + 1);
      
      if (!byClass[sinif]) {
        byClass[sinif] = {
          total: 0,
          completed: 0,
          remaining: 0,
          byQrup: {}
        };
      }
      
      if (!byClass[sinif].byQrup[qrup]) {
        byClass[sinif].byQrup[qrup] = {
          total: 0,
          completed: 0,
          remaining: 0,
          byFenn: {}
        };
      }
    });
    
    // Her sınıf/qrup için TOTAL'i gerçek öğrenci sayısından hesapla
    Object.keys(byClass).forEach(sinif => {
      Object.keys(byClass[sinif].byQrup).forEach(qrup => {
        const key = `${sinif}-${qrup}`;
        const studentCount = studentCountByClassQrup.get(key) || 0;
        // Her öğrenci için 9 soru var (Blok)
        const totalForThisQrup = studentCount * blokQuestionsPerStudent;
        byClass[sinif].byQrup[qrup].total = totalForThisQrup;
        byClass[sinif].total += totalForThisQrup;
      });
    });
    
    // Tamamlanma durumlarını hesapla (SADECE COMPLETED)
    assignments.forEach(a => {
      const sinif = a.sinif;
      const qrup = a.qrup;
      const fenn = a.fenn;
      
      if (byClass[sinif] && byClass[sinif].byQrup[qrup]) {
        // COMPLETED: Tüm yoklamalar (aktif + pasif + admin)
        byClass[sinif].completed += a.completedCount || 0;
        byClass[sinif].byQrup[qrup].completed += a.completedCount || 0;
        
        if (!byClass[sinif].byQrup[qrup].byFenn) {
          byClass[sinif].byQrup[qrup].byFenn = {};
        }
        
        // Eğer bu fenn için zaten istatistik varsa, topla (birden fazla öğretmen olabilir)
        if (!byClass[sinif].byQrup[qrup].byFenn![fenn]) {
          // TOTAL: Gerçek öğrenci sayısından hesapla (atamadan değil!)
          const key = `${sinif}-${qrup}`;
          const studentCount = studentCountByClassQrup.get(key) || 0;
          
          byClass[sinif].byQrup[qrup].byFenn![fenn] = {
            total: studentCount, // SİSTEMDEKİ ÖĞRENCİ SAYISI
            completed: a.completedCount || 0,
            remaining: 0 // Sonra hesaplanacak
          };
        } else {
          // Birden fazla öğretmen aynı fenni yokluyorsa, sadece completed'i topla
          const existing = byClass[sinif].byQrup[qrup].byFenn![fenn];
          existing.completed += a.completedCount || 0;
          // TOTAL değişmez (sistem öğrenci sayısı sabit!)
        }
      }
    });
    
    // Kalan hesapla
    Object.keys(byClass).forEach(sinif => {
      byClass[sinif].remaining = byClass[sinif].total - byClass[sinif].completed;
      
      Object.keys(byClass[sinif].byQrup).forEach(qrup => {
        byClass[sinif].byQrup[qrup].remaining = 
          byClass[sinif].byQrup[qrup].total - byClass[sinif].byQrup[qrup].completed;
        
        // Fenn bazlı remaining'i de hesapla
        if (byClass[sinif].byQrup[qrup].byFenn) {
          Object.keys(byClass[sinif].byQrup[qrup].byFenn!).forEach(fenn => {
            const fennData = byClass[sinif].byQrup[qrup].byFenn![fenn];
            fennData.remaining = fennData.total - fennData.completed;
          });
        }
      });
    });
    
    const stats: YoxlamaStats = {
      totalScannedData,
      totalAssignments,
      activeAssignments,
      completedAssignments,
      passiveAssignments,
      totalTeachers,
      activeTeachers,
      totalCompleted: fennBasedCompleted,
      totalRemaining: fennBasedTotal - fennBasedCompleted,
      adminEmptyPapers: adminEmptyPapers, // Admin boş kağıt sayısı (Blok için)
      totalFennBased: {
        completed: fennBasedCompleted,
        total: fennBasedTotal,
        percentage: Math.round((fennBasedCompleted / fennBasedTotal) * 100) || 0
      },
      totalSystemBased: {
        completed: systemCompleted,
        total: totalSystemCapacity,
        percentage: Math.round((systemCompleted / totalSystemCapacity) * 100) || 0
      },
      byClass
    };
    
    console.log('✅ adminService: İstatistikler hesaplandı:', stats);
    return stats;
  } catch (error) {
    console.error('❌ adminService: İstatistikler hesaplanırken hata:', error);
    throw error;
  }
};

// Belirli bir sınıf, qrup ve subGroup için yoklama verilerini getir
export const getYoxlamaDataForAssignment = async (
  sinif: string,
  qrup: string,
  subGroup?: string
): Promise<ScannedData[]> => {
  try {
    console.log('📚 adminService: Yoklama verileri getiriliyor:', { sinif, qrup, subGroup });
    
    // Grade'i normalize et (string'e çevir)
    const normalizedSinif = normalizeGrade(sinif);
    
    // Hem string hem number olarak sorgula (Firestore'da her iki tip de olabilir!)
    const queryPromises: Promise<any>[] = [];
    
    const scannedRef = collection(db, 'scanned2610');
    
    // String olarak sorgula
    const stringQuery = query(
      scannedRef,
      where('grade', '==', normalizedSinif),
      where('specialtyGroup', '==', qrup)
    );
    queryPromises.push(getDocs(stringQuery));
    
    // Number olarak da sorgula (eğer number'a çevrilebiliyorsa)
    if (!isNaN(Number(sinif))) {
      const numericSinif = Number(sinif);
      const numberQuery = query(
        scannedRef,
        where('grade', '==', numericSinif),
        where('specialtyGroup', '==', qrup)
      );
      queryPromises.push(getDocs(numberQuery));
    }
    
    // Her iki sorguyu paralel çalıştır ve sonuçları birleştir
    const querySnapshots = await Promise.all(queryPromises);
    
    // Tüm sonuçları birleştir (duplicate'leri çıkar - document ID'ye göre)
    const uniqueDocs = new Map<string, QueryDocumentSnapshot<DocumentData>>();
    querySnapshots.forEach(snapshot => {
      snapshot.docs.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
        if (!uniqueDocs.has(doc.id)) {
          uniqueDocs.set(doc.id, doc);
        }
      });
    });
    
    // Map'ten QuerySnapshot benzeri bir yapı oluştur
    const querySnapshot = {
      docs: Array.from(uniqueDocs.values()),
      forEach: (callback: (doc: QueryDocumentSnapshot<DocumentData>) => void) => {
        uniqueDocs.forEach((doc) => callback(doc));
      },
      empty: uniqueDocs.size === 0
    } as any;
    const data: ScannedData[] = [];
    
    querySnapshot.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
      const docData = doc.data() as ScannedData;
      
      // SubGroup varsa, subGroup'a göre filtrele
      if (subGroup) {
        // SubGroup normalize ederek karşılaştır (RI -> Rİ)
        const normalizedExpected = normalizeSubGroup(subGroup);
        const normalizedStudent = normalizeSubGroup(docData.subGroup);
        if (normalizedExpected === normalizedStudent) {
          data.push(docData);
        }
      } else {
        // SubGroup yoksa tümünü al
        data.push(docData);
      }
    });
    
    console.log('✅ adminService: Yoklama verileri getirildi:', data.length);
    return data;
  } catch (error) {
    console.error('❌ adminService: Yoklama verileri getirilirken hata:', error);
    throw error;
  }
};

// Tüm öğrencileri getir (detaylı bilgilerle)
export interface StudentDetailData extends ScannedData {
  id: string; // Document ID
  fennScores?: {
    [fennName: string]: {
      hoca?: string;
      hocaId?: string;
      scores: { [questionNumber: string]: string };
      totalScore?: number;
    };
  };
  completedFennCount?: number;
  totalFennCount?: number;
  [key: string]: any; // For Q##_c buraxilis grading fields
}

export const getAllStudents = async (collectionName: string = 'scanned2610'): Promise<StudentDetailData[]> => {
  try {
    console.log('📚 adminService: Tüm öğrenciler getiriliyor... Collection:', collectionName);
    
    const scannedRef = collection(db, collectionName);
    const querySnapshot = await getDocs(scannedRef);
    
    const students: StudentDetailData[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Öğrencinin qrupuna göre fennleri bul
      const qrupConfig = QRUP_CONFIGS.find(q => q.qrup === data.specialtyGroup);
      const qrupFennler = qrupConfig?.fennler || [];
      
      // Öğrencinin subGroup'una göre filtreleme
      const studentSubGroup = data.subGroup && data.subGroup !== '-' ? data.subGroup : null;
      
      // SubGroup normalize fonksiyonu (RI -> Rİ, ri -> Rİ vb.)
      const normalizeSubGroup = (sg: string | null): string | null => {
        if (!sg) return null;
        return sg.toUpperCase().replace('I', 'İ'); // Normal I'yı Türkçe İ'ye çevir
      };
      
      const normalizedStudentSubGroup = normalizeSubGroup(studentSubGroup);
      
      const applicableFennler = qrupFennler.filter(fennRange => {
        if (normalizedStudentSubGroup) {
          // SubGroup varsa, sadece o subGroup'un fennleri veya subGroup olmayan fennler
          const normalizedFennSubGroup = normalizeSubGroup(fennRange.subGroup || null);
          return !fennRange.subGroup || normalizedFennSubGroup === normalizedStudentSubGroup;
        } else {
          // SubGroup yoksa, sadece subGroup olmayan fennler
          return !fennRange.subGroup;
        }
      });
      
      const totalFennCount = applicableFennler.length; // Her öğrenci için 3 fenn
      
      // Fenn skorlarını topla - sadece öğrencinin qrupundaki fennlere bak
      const fennScores: StudentDetailData['fennScores'] = {};
      let completedFennCount = 0;
      
      applicableFennler.forEach(fennRange => {
        const fenn = fennRange.fenn;
        if (data[fenn] && typeof data[fenn] === 'object') {
          const fennData = data[fenn];
          const scores: { [questionNumber: string]: string } = {};
          let totalScore = 0;
          
          Object.keys(fennData).forEach(key => {
            if (key !== 'hocaId' && key !== 'hoca') {
              scores[key] = fennData[key];
              // Sayısal değeri topla
              const scoreValue = parseFloat(fennData[key]);
              if (!isNaN(scoreValue)) {
                totalScore += scoreValue;
              }
            }
          });
          
          fennScores[fenn] = {
            hoca: fennData.hoca,
            hocaId: fennData.hocaId,
            scores,
            totalScore
          };
          
          completedFennCount++;
        }
      });
      
      // Q## / Q##_c — böyük-kiçik hərf fərqini də canonical ada çevir (Q26, Q26_c)
      const buraxilisFields = collectQuestionFields(data);

      const rawSpecialty = pickStudentField(data, 'specialtyGroup', 'qrup');
      const specialtyGroup = isBlankGroup(rawSpecialty) ? '' : String(rawSpecialty);
      const rawSubGroup = pickStudentField(data, 'subGroup');
      const subGroup = isBlankGroup(rawSubGroup) ? '' : String(rawSubGroup);

      const studentData: StudentDetailData = {
        id: doc.id,
        arka_uz: data.arka_uz || '',
        arka_uz_sag: data.arka_uz_sag || '',
        arka_uz_sol: data.arka_uz_sol || '',
        firstName: pickStudentField(data, 'firstName', 'first_name', 'ad', 'name') || '',
        grade: normalizeGrade(pickStudentField(data, 'grade', 'sinif', 'class')),
        lastName: pickStudentField(data, 'lastName', 'last_name', 'soyad', 'surname') || '',
        mode: data.mode || '',
        on_uz: data.on_uz || '',
        on_uz_sag: data.on_uz_sag || '',
        on_uz_sol: data.on_uz_sol || '',
        scanned_at: data.scanned_at || null,
        specialtyGroup,
        status: data.status || '',
        studentType: data.studentType || '',
        student_id: String(pickStudentField(data, 'student_id', 'studentId', 'sagird_id') || doc.id),
        subGroup,
        variant: pickStudentField(data, 'variant') || '',
        xdil: pickStudentField(data, 'xdil', 'xarici_dil', 'foreignLanguage') || '',
        buildingName: pickStudentField(data, 'buildingName', 'building') || '',
        '1_path': data['1_path'] || pickStudentField(data, '1_path') || '',
        '2_path': data['2_path'] || pickStudentField(data, '2_path') || '',
        questions: data.questions,
        question_count: data.question_count,
        fennScores,
        completedFennCount,
        totalFennCount,
        ...buraxilisFields
      };
      
      // İlk öğrenci için debug log
      if (students.length === 0) {
        console.log('🔍 adminService: İlk öğrenci verisi:', {
          id: doc.id,
          variant: data.variant,
          xdil: data.xdil,
          grade: data.grade,
          buraxilisFieldCount: Object.keys(buraxilisFields).length,
          buraxilisFields: Object.keys(buraxilisFields)
        });
      }
      
      students.push(studentData);
    });
    
    // Document ID'lerine göre sırala (Firestore'da oluşturulma sırasına yakın bir sıralama)
    // NOT: Sisteme eklenme sırasına göre göstermek için document ID'lerine göre sıralıyoruz
    students.sort((a, b) => {
      // Document ID'lerini karşılaştır (lexicographic sıralama)
      return a.id.localeCompare(b.id);
    });
    
    console.log('✅ adminService: Öğrenciler başarıyla getirildi:', students.length);
    return students;
  } catch (error) {
    console.error('❌ adminService: Öğrenciler getirilirken hata:', error);
    throw error;
  }
};

// Belirli bir öğrencinin detaylı bilgilerini getir
export const getStudentDetails = async (studentId: string): Promise<StudentDetailData | null> => {
  try {
    console.log('📚 adminService: Öğrenci detayları getiriliyor:', studentId);
    
    const docRef = doc(db, 'scanned2610', studentId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      console.log('⚠️ adminService: Öğrenci bulunamadı');
      return null;
    }
    
    const data = docSnap.data();
    
    // Öğrencinin qrupuna göre fennleri bul
    const qrupConfig = QRUP_CONFIGS.find(q => q.qrup === data.specialtyGroup);
    const qrupFennler = qrupConfig?.fennler || [];
    
    // Öğrencinin subGroup'una göre filtreleme
    const studentSubGroup = data.subGroup && data.subGroup !== '-' ? data.subGroup : null;
    
    // SubGroup normalize fonksiyonu (RI -> Rİ, ri -> Rİ vb.)
    const normalizeSubGroup = (sg: string | null): string | null => {
      if (!sg) return null;
      return sg.toUpperCase().replace('I', 'İ'); // Normal I'yı Türkçe İ'ye çevir
    };
    
    const normalizedStudentSubGroup = normalizeSubGroup(studentSubGroup);
    
    const applicableFennler = qrupFennler.filter(fennRange => {
      if (normalizedStudentSubGroup) {
        // SubGroup varsa, sadece o subGroup'un fennleri veya subGroup olmayan fennler
        const normalizedFennSubGroup = normalizeSubGroup(fennRange.subGroup || null);
        return !fennRange.subGroup || normalizedFennSubGroup === normalizedStudentSubGroup;
      } else {
        // SubGroup yoksa, sadece subGroup olmayan fennler
        return !fennRange.subGroup;
      }
    });
    
    const totalFennCount = applicableFennler.length; // Her öğrenci için 3 fenn
    
    // Fenn skorlarını topla - sadece öğrencinin qrupundaki fennlere bak
    const fennScores: StudentDetailData['fennScores'] = {};
    let completedFennCount = 0;
    
    applicableFennler.forEach(fennRange => {
      const fenn = fennRange.fenn;
      if (data[fenn] && typeof data[fenn] === 'object') {
        const fennData = data[fenn];
        const scores: { [questionNumber: string]: string } = {};
        let totalScore = 0;
        
        Object.keys(fennData).forEach(key => {
          if (key !== 'hocaId' && key !== 'hoca') {
            scores[key] = fennData[key];
            const scoreValue = parseFloat(fennData[key]);
            if (!isNaN(scoreValue)) {
              totalScore += scoreValue;
            }
          }
        });
        
        fennScores[fenn] = {
          hoca: fennData.hoca,
          hocaId: fennData.hocaId,
          scores,
          totalScore
        };
        
        completedFennCount++;
      }
    });
    
    const studentData: StudentDetailData = {
      id: docSnap.id,
      arka_uz: data.arka_uz || '',
      arka_uz_sag: data.arka_uz_sag || '',
      arka_uz_sol: data.arka_uz_sol || '',
      firstName: data.firstName || '',
      grade: data.grade || '',
      lastName: data.lastName || '',
      mode: data.mode || '',
      on_uz: data.on_uz || '',
      on_uz_sag: data.on_uz_sag || '',
      on_uz_sol: data.on_uz_sol || '',
      scanned_at: data.scanned_at || null,
      specialtyGroup: data.specialtyGroup || '',
      status: data.status || '',
      studentType: data.studentType || '',
      student_id: data.student_id || '',
      subGroup: data.subGroup || '',
      fennScores,
      completedFennCount,
      totalFennCount // Artık her öğrenci için 3!
    };
    
    console.log('✅ adminService: Öğrenci detayları getirildi');
    return studentData;
  } catch (error) {
    console.error('❌ adminService: Öğrenci detayları getirilirken hata:', error);
    throw error;
  }
};

// Admin tarafından boş kağıt olarak işaretle (tüm soruları 0'la)
export const setFennToZero = async (studentId: string, fenn: string, adminName: string): Promise<void> => {
  try {
    console.log('💾 adminService: Fenn boş kağıt olarak işaretleniyor:', studentId, fenn);
    
    // Qrup bilgisini al
    const docRef = doc(db, 'scanned2610', studentId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Öğrenci bulunamadı');
    }
    
    const studentData = docSnap.data();
    const qrup = studentData.specialtyGroup;
    
    // O qrup ve fenn için soru sayısını bul
    const qrupConfig = QRUP_CONFIGS.find(q => q.qrup === qrup);
    const fennRange = qrupConfig?.fennler.find(f => f.fenn === fenn);
    
    if (!fennRange) {
      throw new Error('Fenn bilgisi bulunamadı');
    }
    
    // Tüm soruları belirle (son 3 soru)
    const questionNumbers = [
      fennRange.end - 2,
      fennRange.end - 1,
      fennRange.end
    ];
    
    // Tüm sorulara 0 ata ve not ekle
    const scores: { [key: string]: string } = {};
    questionNumbers.forEach(num => {
      scores[num.toString()] = '0';
    });
    
    // Boş kağıt notu ekle (ilk soruya)
    scores[`${questionNumbers[0]}_note`] = 'Boş kağıt';
    
    // Kaydet (Admin bilgileriyle)
    await updateDoc(docRef, {
      [fenn]: {
        ...scores,
        hocaId: 'admin',
        hoca: `Admin - ${adminName}`
      }
    });
    
    console.log('✅ adminService: Fenn boş kağıt olarak işaretlendi');
  } catch (error) {
    console.error('❌ adminService: Fenn boş kağıt olarak işaretlenirken hata:', error);
    throw error;
  }
};

// Admin tarafından birden fazla fenni boş kağıt olarak işaretle (toplu işlem)
export const setMultipleFennToZero = async (
  studentId: string, 
  fennList: string[], 
  adminName: string
): Promise<{ success: number; failed: number; errors: string[] }> => {
  try {
    console.log('💾 adminService: Birden fazla fenn boş kağıt olarak işaretleniyor:', studentId, fennList);
    
    // Qrup bilgisini al
    const docRef = doc(db, 'scanned2610', studentId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Öğrenci bulunamadı');
    }
    
    const studentData = docSnap.data();
    const qrup = studentData.specialtyGroup;
    
    // Qrup config'i bul
    const qrupConfig = QRUP_CONFIGS.find(q => q.qrup === qrup);
    if (!qrupConfig) {
      throw new Error('Qrup konfigürasyonu bulunamadı');
    }
    
    let successCount = 0;
    let failedCount = 0;
    const errors: string[] = [];
    
    // Tüm fennler için update objesi oluştur
    const updateData: { [key: string]: any } = {};
    
    for (const fenn of fennList) {
      try {
        // Fenn'in range'ini bul
        const fennRange = qrupConfig.fennler.find(f => f.fenn === fenn);
        
        if (!fennRange) {
          failedCount++;
          errors.push(`${fenn}: Fenn bilgisi bulunamadı`);
          continue;
        }
        
        // Tüm soruları belirle (son 3 soru)
        const questionNumbers = [
          fennRange.end - 2,
          fennRange.end - 1,
          fennRange.end
        ];
        
        // Tüm sorulara 0 ata ve not ekle
        const scores: { [key: string]: string } = {};
        questionNumbers.forEach(num => {
          scores[num.toString()] = '0';
        });
        
        // Boş kağıt notu ekle (ilk soruya)
        scores[`${questionNumbers[0]}_note`] = 'Boş kağıt';
        
        // Update data'ya ekle
        updateData[fenn] = {
          ...scores,
          hocaId: 'admin',
          hoca: `Admin - ${adminName}`
        };
        
        successCount++;
      } catch (fennError: any) {
        failedCount++;
        errors.push(`${fenn}: ${fennError.message}`);
        console.error(`❌ ${fenn} işlenirken hata:`, fennError);
      }
    }
    
    // Eğer update edilecek veri varsa, tek seferde kaydet
    if (Object.keys(updateData).length > 0) {
      await updateDoc(docRef, updateData);
      console.log('✅ adminService: Fennler boş kağıt olarak işaretlendi:', Object.keys(updateData));
    }
    
    return {
      success: successCount,
      failed: failedCount,
      errors
    };
  } catch (error) {
    console.error('❌ adminService: Fennler boş kağıt olarak işaretlenirken hata:', error);
    throw error;
  }
};



// ─── İş Nömrəsi Prefix Filter Functions ────────────────────────────────

/**
 * Queries students matching assignment criteria and returns available
 * student_id prefixes (first 3 digits) with counts.
 */
export const getStudentPrefixesForAssignment = async (
  collectionName: string,
  sinif: string,
  variant: string,
  buildingName?: string,
  fenn?: string
): Promise<PrefixInfo[]> => {
  const studentsRef = collection(db, collectionName);
  const normalizedSinif = normalizeGrade(sinif);
  const numericSinif = Number(normalizedSinif);

  const buildingConstraints = (buildingName && buildingName !== 'Hamısı')
    ? [where('buildingName', '==', buildingName)]
    : [];

  const stringQuery = query(
    studentsRef,
    where('grade', '==', normalizedSinif),
    where('variant', '==', variant),
    ...buildingConstraints
  );
  const prefixQueryPromises: Promise<any>[] = [getDocs(stringQuery)];

  if (!isNaN(numericSinif)) {
    const numericQuery = query(
      studentsRef,
      where('grade', '==', numericSinif),
      where('variant', '==', variant),
      ...buildingConstraints
    );
    prefixQueryPromises.push(getDocs(numericQuery));
  }

  const prefixQuerySnapshots = await Promise.all(prefixQueryPromises);

  const prefixUniqueDocMap = new Map<string, any>();
  prefixQuerySnapshots.forEach(snapshot => {
    snapshot.docs.forEach((d: any) => {
      if (!prefixUniqueDocMap.has(d.id)) prefixUniqueDocMap.set(d.id, d);
    });
  });

  let docs = Array.from(prefixUniqueDocMap.values()).map((d: any) => d.data());

  // Apply xdil filter if foreign language
  if (fenn) {
    const fennXdilNorm = normalizeXdil(fenn);
    if (['İngilis dili', 'Fransız dili', 'Alman dili', 'Rus dili'].includes(fennXdilNorm)) {
      docs = docs.filter(d => normalizeXdil(d.xdil) === fennXdilNorm);
    }
  }

  return extractUniquePrefixes(docs as Array<{ student_id?: string; [key: string]: any }>);
};

/**
 * Updates the prefix filter on a Buraxılış assignment and recalculates totalCount.
 * Passing null or { enabled: false } disables the filter.
 */
export const updateAssignmentPrefixFilter = async (
  assignmentId: string,
  newFilter: StudentIdPrefixFilter | null
): Promise<{ success: boolean; message: string }> => {
  try {
    const assignmentRef = doc(db, 'yoxla-s', assignmentId);
    const assignmentSnap = await getDoc(assignmentRef);
    if (!assignmentSnap.exists()) {
      return { success: false, message: 'Təyinat tapılmadı' };
    }
    const data = assignmentSnap.data();
    if (data.examType !== 'buraxilis') {
      return { success: false, message: 'Prefix filtr yalnız buraxılış təyinatları üçün mümkündür' };
    }

    // Recalculate totalCount with the new filter
    const collectionName = data.collectionName || 'scanned2610';
    const sinif = normalizeGrade(data.sinif);
    const variant = data.variant;

    const studentsRef = collection(db, collectionName);
    const numericSinif = Number(sinif);

    const buildingConstraints = (data.buildingName && data.buildingName !== 'Hamısı')
      ? [where('buildingName', '==', data.buildingName)]
      : [];

    const updateStringQuery = query(
      studentsRef,
      where('grade', '==', sinif),
      where('variant', '==', variant),
      ...buildingConstraints
    );
    const updateQueryPromises: Promise<any>[] = [getDocs(updateStringQuery)];

    if (!isNaN(numericSinif)) {
      const updateNumericQuery = query(
        studentsRef,
        where('grade', '==', numericSinif),
        where('variant', '==', variant),
        ...buildingConstraints
      );
      updateQueryPromises.push(getDocs(updateNumericQuery));
    }

    const updateQuerySnapshots = await Promise.all(updateQueryPromises);

    const updateUniqueDocMap = new Map<string, any>();
    updateQuerySnapshots.forEach(snapshot => {
      snapshot.docs.forEach((d: any) => {
        if (!updateUniqueDocMap.has(d.id)) updateUniqueDocMap.set(d.id, d);
      });
    });

    const effectiveFilter = (newFilter && newFilter.enabled && newFilter.prefixes.length > 0) ? newFilter : null;

    let filteredDocs = Array.from(updateUniqueDocMap.values());
    if (effectiveFilter) {
      filteredDocs = filteredDocs.filter(d => matchesPrefixFilter(d.data().student_id, effectiveFilter));
    }

    // Apply xdil filter if foreign language
    let totalStudents = filteredDocs.length;
    const fennXdilNorm = normalizeXdil(data.fenn);
    if (['İngilis dili', 'Fransız dili', 'Alman dili', 'Rus dili'].includes(fennXdilNorm)) {
      totalStudents = filteredDocs.filter(d => normalizeXdil(d.data().xdil) === fennXdilNorm).length;
    }

    const updateData: any = {
      totalCount: totalStudents,
      updatedAt: Timestamp.now()
    };

    if (effectiveFilter) {
      updateData.studentIdPrefixFilter = effectiveFilter;
    } else {
      updateData.studentIdPrefixFilter = deleteField();
    }

    await updateDoc(assignmentRef, updateData);

    const message = effectiveFilter
      ? `Prefix filtr yeniləndi: ${effectiveFilter.prefixes.length} prefiks, ${totalStudents} şagird`
      : `Prefix filtr silindi, ${totalStudents} şagird`;

    return { success: true, message };
  } catch (error) {
    console.error('❌ adminService: Prefix filtr yenilənərkən xəta:', error);
    throw error;
  }
};